import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Printer, Loader2, X } from "lucide-react";

/**
 * ShippingLabel — iteration 116
 *
 * Standalone printable route rendered at ``/orders/:id/label``. Opens in
 * a new browser window, auto-invokes ``window.print()`` after the order
 * hydrates. A ``@page`` CSS block sizes the paper to 100mm × 150mm
 * (standard Royal Mail / DPD sticky-label footprint) with 4mm margins
 * so a home printer can output straight onto sticky label paper — or
 * users can select "Save as PDF" in the print dialog.
 *
 * Layout (top → bottom):
 *   - Return address (tiny, top-left)
 *   - Order ref & courier badge (top-right)
 *   - Big recipient block (recipient name → line 1 → line 2 → city,
 *     postcode → country)
 *   - QR/barcode placeholder (bottom-left)
 *   - "Print" + "Close" controls (hidden with `@media print`)
 */
export default function ShippingLabel() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [err, setErr] = useState("");
  const [autoPrinted, setAutoPrinted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/orders/${id}`);
        setOrder(data);
      } catch (e) {
        setErr(formatApiError(e));
      }
    })();
  }, [id]);

  // Auto-invoke print once the label is on screen. Small delay so the
  // browser paints the layout before opening the dialog.
  useEffect(() => {
    if (!order || autoPrinted) return;
    const t = setTimeout(() => { window.print(); setAutoPrinted(true); }, 400);
    return () => clearTimeout(t);
  }, [order, autoPrinted]);

  if (err) {
    return (
      <div className="p-10 text-center text-rose-600" data-testid="label-error">{err}</div>
    );
  }
  if (!order) {
    return (
      <div className="p-10 text-center text-slate-500 flex items-center justify-center gap-2" data-testid="label-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Preparing label…
      </div>
    );
  }

  const addr = order.delivery_address || {};
  const recipient = order.recipient_name || addr.recipient_name || "";
  const line1 = addr.line1 || addr.street || "";
  const line2 = addr.line2 || "";
  const city = addr.city || "";
  const postcode = addr.postcode || "";
  const country = addr.country || "GB";
  const countryLabel = country && country.toUpperCase() !== "GB" ? country : "United Kingdom";
  const orderRef = (order.id || "").toString().slice(0, 12).toUpperCase();
  const sellerName = order.seller_kid_name || order.seller_name || "Seller";
  const sellerCity = order.seller_city || "";
  const courier = order.package_size ? `${order.package_size}`.replace(/_/g, " ") : "";
  // Deterministic 12×12 QR placeholder pattern derived from the order
  // ref so every reprint shows the same dots. Not a functional QR
  // (per spec: "clean QR/Barcode placeholder for order lookup") — the
  // human-readable ref below the block is the real lookup key.
  const qrDots = (() => {
    const seed = orderRef.padEnd(24, "X");
    const grid = [];
    for (let y = 0; y < 12; y++) {
      const row = [];
      for (let x = 0; x < 12; x++) {
        const c = seed.charCodeAt((y * 12 + x) % seed.length);
        // Anchor squares in the three finder corners (top-left, top-right, bottom-left).
        const isFinder =
          (x < 3 && y < 3) || (x > 8 && y < 3) || (x < 3 && y > 8);
        row.push(isFinder ? 1 : (c % 3 === 0 ? 1 : 0));
      }
      grid.push(row);
    }
    return grid;
  })();

  return (
    <div className="label-outer" data-testid="shipping-label">
      {/* Print-only page + screen preview styles */}
      <style>{`
        @page { size: 100mm 150mm; margin: 4mm; }
        @media print {
          html, body { margin: 0; padding: 0; background: #fff; }
          .no-print { display: none !important; }
          .label-outer { box-shadow: none !important; border: none !important; margin: 0 !important; padding: 0 !important; }
          .label-card { width: 92mm; height: 142mm; page-break-after: always; }
        }
        @media screen {
          body { background: #f1f5f9; }
          .label-outer { display: flex; flex-direction: column; align-items: center; padding: 24px; gap: 16px; min-height: 100vh; }
          .label-card { width: 92mm; height: 142mm; box-shadow: 0 12px 30px -8px rgba(15, 23, 42, 0.25); border-radius: 6px; }
        }
        .label-card {
          background: #fff;
          color: #0f172a;
          padding: 5mm;
          box-sizing: border-box;
          font-family: 'Inter', ui-sans-serif, system-ui, sans-serif;
          font-size: 3.2mm;
          line-height: 1.25;
          display: flex;
          flex-direction: column;
          border: 1px solid #cbd5e1;
        }
        .label-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 4mm; padding-bottom: 3mm; border-bottom: 1px dashed #94a3b8; }
        .label-return { font-size: 2.6mm; color: #475569; max-width: 45mm; }
        .label-order { font-size: 2.6mm; color: #475569; text-align: right; text-transform: uppercase; letter-spacing: 0.5px; }
        .label-order strong { display: block; color: #0f172a; font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 3.6mm; letter-spacing: 1px; margin-top: 0.5mm; }
        .label-body { flex: 1; padding: 6mm 0 4mm; display: flex; flex-direction: column; justify-content: flex-start; }
        .label-body .to { font-size: 2.6mm; text-transform: uppercase; letter-spacing: 0.6px; color: #475569; margin-bottom: 1.5mm; }
        .label-recipient { font-size: 5.2mm; font-weight: 800; letter-spacing: 0.2px; margin-bottom: 2mm; word-break: break-word; }
        .label-address { font-size: 4mm; font-weight: 600; margin-bottom: 0.8mm; }
        .label-country { font-size: 3.6mm; text-transform: uppercase; letter-spacing: 1px; margin-top: 1.5mm; font-weight: 700; }
        .label-bottom { display: flex; justify-content: space-between; align-items: flex-end; gap: 4mm; padding-top: 3mm; border-top: 1px dashed #94a3b8; }
        .label-qr { display: grid; grid-template-columns: repeat(12, 1fr); grid-template-rows: repeat(12, 1fr); width: 24mm; height: 24mm; gap: 0; }
        .label-qr div { width: 100%; height: 100%; }
        .label-qr div.on { background: #0f172a; }
        .label-meta { text-align: right; font-size: 2.6mm; color: #475569; }
        .label-meta strong { color: #0f172a; font-size: 3.2mm; }
      `}</style>

      {/* Screen-only controls */}
      <div className="no-print flex items-center gap-2" data-testid="label-controls">
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm shadow-md"
          data-testid="label-print-btn"
        >
          <Printer className="w-4 h-4" /> Print / Save as PDF
        </button>
        <button
          type="button"
          onClick={() => window.close()}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-300 hover:border-slate-500 text-slate-700 font-bold text-sm shadow-sm"
          data-testid="label-close-btn"
        >
          <X className="w-4 h-4" /> Close
        </button>
      </div>

      <div className="label-card" data-testid="label-card">
        <div className="label-top">
          <div className="label-return" data-testid="label-return">
            <div style={{ fontWeight: 700, color: "#0f172a" }}>Return to:</div>
            <div>{sellerName}</div>
            {sellerCity && <div>{sellerCity}</div>}
            <div style={{ marginTop: "1mm", fontSize: "2.4mm", color: "#64748b" }}>MyTableTopApp · UK</div>
          </div>
          <div className="label-order">
            Order ref
            <strong data-testid="label-order-ref">#{orderRef}</strong>
            {courier && <div style={{ marginTop: "1mm", textTransform: "uppercase" }}>{courier}</div>}
          </div>
        </div>
        <div className="label-body">
          <div className="to">DELIVER TO</div>
          <div className="label-recipient" data-testid="label-recipient">{recipient || "—"}</div>
          {line1 && <div className="label-address" data-testid="label-line1">{line1}</div>}
          {line2 && <div className="label-address" data-testid="label-line2">{line2}</div>}
          <div className="label-address" data-testid="label-city">
            {city}{postcode ? `, ${postcode}` : ""}
          </div>
          <div className="label-country" data-testid="label-country">{countryLabel}</div>
        </div>
        <div className="label-bottom">
          <div className="label-qr" role="img" aria-label={`QR placeholder for order ${orderRef}`} data-testid="label-qr">
            {qrDots.flat().map((v, i) => <div key={i} className={v ? "on" : ""} />)}
          </div>
          <div className="label-meta">
            <div>Scan ref</div>
            <strong>#{orderRef}</strong>
            <div style={{ marginTop: "1mm", fontSize: "2.4mm" }}>mytabletopapp.co.uk</div>
          </div>
        </div>
      </div>
    </div>
  );
}
